import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "utils/api";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Dropdown from "components/CustomDropdown/Dropdown";

function EditVideo() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    user: "",
    videoType: "",
    description: "",
    videoSourceType: "",
    videoLink: "",
  });
  const [videoFile, setVideoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");

  useEffect(() => {
    fetchVideoData();
  }, [id]);

  const fetchVideoData = async () => {
    try {
      setFetchLoading(true);
      const response = await api.get(`/videos/${id}`);
      if (response.data.success) {
        const video = response.data.data;
        setFormData({
          title: video.title || "",
          user: video.user || "",
          videoType: video.videoType || "",
          description: video.description || "",
          videoSourceType: video.videoSourceType || "YOUTUBE",
          videoLink: video.videoLink || "",
        });
        setBannerPreview(video.banner || "");
      } else {
        toast.error("Failed to fetch video data");
        navigate("/allvideos");
      }
    } catch (error) {
      console.error("Error fetching video:", error);
      toast.error(error.response?.data?.message || "Failed to fetch video data");
      navigate("/allvideos");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear video file when source type changes
    if (name === "videoSourceType") {
      setVideoFile(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["video/mp4", "video/mpeg", "video/quicktime", "video/x-msvideo"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a valid video file (MP4, MPEG, MOV, AVI)");
        return;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error("Video file size should not exceed 100MB");
        return;
      }
      setVideoFile(file);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload a valid banner image");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Banner image size should not exceed 5MB");
        return;
      }
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Please enter video title");
      return;
    }
    if (!formData.user) {
      toast.error("Please select user");
      return;
    }
    if (!formData.videoType) {
      toast.error("Please select video type");
      return;
    }
    if (!formData.videoSourceType) {
      toast.error("Please select video source type");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please enter description");
      return;
    }

    if (formData.videoSourceType === "YOUTUBE" && !formData.videoLink.trim()) {
      toast.error("Please enter YouTube video link");
      return;
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("user", formData.user);
      formDataToSend.append("videoType", formData.videoType);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("videoSourceType", formData.videoSourceType);

      if (bannerFile) {
        formDataToSend.append("banner", bannerFile);
      }

      if (formData.videoSourceType === "NORMAL" && videoFile) {
        formDataToSend.append("video", videoFile);
      } else if (formData.videoSourceType === "YOUTUBE") {
        formDataToSend.append("videoLink", formData.videoLink);
      }

      const response = await api.put(`/videos/${id}`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Video updated successfully");
        navigate("/allvideos");
      } else {
        toast.error(response.data.message || "Failed to update video");
      }
    } catch (error) {
      console.error("Error updating video:", error);
      toast.error(error.response?.data?.message || "Failed to update video");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress color="info" />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h5" fontWeight="medium" mb={3}>
                  Edit Video
                </ArgonTypography>

                <ArgonBox component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Title */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Title *
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonInput
                        type="text"
                        placeholder="Enter video title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>

                    {/* User */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          User *
                        </ArgonTypography>
                      </ArgonBox>
                      <Dropdown
                        value={formData.user}
                        options={[
                          { label: "Select user", value: "" },
                          { label: "Parent", value: "Parent" },
                          { label: "Tutor", value: "Tutor" },
                        ]}
                        onChange={(e) => setFormData({ ...formData, user: e.target.value })}
                        width={800}
                        size="small"
                      />
                    </Grid>

                    {/* Banner */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Banner Image
                        </ArgonTypography>
                        <ArgonTypography display="block" variant="caption" color="text">
                          Recommended size: 1280 x 720 px (16:9), max 5MB.
                        </ArgonTypography>
                      </ArgonBox>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBannerChange}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d2d6da",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      />
                      {bannerPreview && (
                        <ArgonBox mt={2}>
                          <img
                            src={bannerPreview}
                            alt="Banner preview"
                            style={{
                              width: "180px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #d2d6da",
                            }}
                          />
                        </ArgonBox>
                      )}
                    </Grid>

                    {/* Video Type */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Type *
                        </ArgonTypography>
                      </ArgonBox>
                      <Dropdown
                        value={formData.videoType}
                        options={[
                          { label: "Select video type", value: "" },
                          { label: "Educational", value: "EDUCATIONAL" },
                          { label: "Tutorial", value: "TUTORIAL" },
                          { label: "Practical", value: "PRACTICAL" },
                        ]}
                        onChange={(e) => setFormData({ ...formData, videoType: e.target.value })}
                        width={800}
                        size="small"
                      />
                    </Grid>

                    {/* Video Source Type */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Source Type *
                        </ArgonTypography>
                      </ArgonBox>
                      <Dropdown
                        value={formData.videoSourceType}
                        options={[
                          { label: "Select video source type", value: "" },
                          { label: "Upload Video (Normal)", value: "NORMAL" },
                          { label: "YouTube Link", value: "YOUTUBE" },
                        ]}
                        onChange={(e) =>
                          setFormData({ ...formData, videoSourceType: e.target.value })
                        }
                        width={800}
                        size="small"
                      />
                    </Grid>

                    {/* Conditional: Video Upload or YouTube Link */}
                    {formData.videoSourceType === "NORMAL" && (
                      <Grid item xs={12}>
                        <ArgonBox mb={1}>
                          <ArgonTypography variant="caption" fontWeight="bold" color="text">
                            Upload New Video File (Optional)
                          </ArgonTypography>
                        </ArgonBox>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleFileChange}
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "1px solid #d2d6da",
                            borderRadius: "8px",
                            fontSize: "14px",
                          }}
                        />
                        {videoFile && (
                          <ArgonTypography variant="caption" color="success" mt={1}>
                            Selected: {videoFile.name}
                          </ArgonTypography>
                        )}
                        {!videoFile && formData.videoLink && (
                          <ArgonTypography variant="caption" color="info" mt={1}>
                            Current: {formData.videoLink.split("/").pop()}
                          </ArgonTypography>
                        )}
                      </Grid>
                    )}

                    {formData.videoSourceType === "YOUTUBE" && (
                      <Grid item xs={12}>
                        <ArgonBox mb={1}>
                          <ArgonTypography variant="caption" fontWeight="bold" color="text">
                            YouTube Video Link *
                          </ArgonTypography>
                        </ArgonBox>
                        <ArgonInput
                          type="text"
                          placeholder="Enter YouTube video URL"
                          name="videoLink"
                          value={formData.videoLink}
                          onChange={handleChange}
                          fullWidth
                        />
                      </Grid>
                    )}

                    {/* Description */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Description *
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonInput
                        multiline
                        rows={4}
                        placeholder="Enter video description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={12}>
                      <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
                        <ArgonButton
                          variant="outlined"
                          color="secondary"
                          onClick={() => navigate("/allvideos")}
                          disabled={loading}
                        >
                          Cancel
                        </ArgonButton>
                        <ArgonButton
                          type="submit"
                          variant="gradient"
                          color="info"
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Update Video"}
                        </ArgonButton>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </ArgonBox>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditVideo;
